import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Icon, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormSelect from 'components-desktop/FormSelect';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TableCaregivers from './components/TableCaregivers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class SearchExpireCaregiversPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      coordinators: [],
      teams: [],

      tableKey: null,
      caregivers: null,
      isSearchButtonEnable: true,
    };
  }

  componentDidMount() {
    this.getSearchCriteria();
  }

  getTitle() {
    return 'Search Expire Caregivers';
  }

  async getSearchCriteria() {
    const promise1 = this.callServiceForObjects('/coordinator/get_id_names');
    const promise2 = this.callServiceForObjects('/team/get_id_names');

    const results = await Promise.all([promise1, promise2]);

    this.setState({
      coordinators: results[0],
      teams: results[1],
    });
  }

  async searchCaregivers(values) {
    try {
      this.setState({
        isSearchButtonEnable: false,
      });

      const params = {
        coordinatorId: helpers.extraFormSelectValue(values, 'coordinatorId'),
        teamId: helpers.extraFormSelectValue(values, 'teamId'),
        field: helpers.extraFormSelectValue(values, 'field'),
        expireStatus: helpers.extraFormSelectValue(values, 'expireStatus'),
      };

      const caregivers = await this.callServiceForRemoteObjects('/caregiver/get_expire_list', params);

      this.setState({
        tableKey: `${Date.now()}_${Math.random()}`,
        caregivers,
      });
    } finally {
      this.setState({
        isSearchButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.searchCaregivers(values);
      }
    });
  }

  renderContent() {
    const { form } = this.props;
    const {
      coordinators,
      teams,
      tableKey,
      caregivers,
      isSearchButtonEnable,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="green_haze"
          title="Search Expire Caregivers by Criteria"
          icon="user-add"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="coordinatorId"
                    label="Coordinator"
                    placeholder="Select..."
                    options={coordinators}
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="teamId"
                    label="Team"
                    placeholder="Select..."
                    options={teams}
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="field"
                    label="Expire Type to Search"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue="physicalExam"
                    options={[
                      { value: 'physicalExam', text: 'Physical Exam' },
                      { value: 'orientation', text: 'Orientation' },
                      { value: 'inServiceTraining', text: 'In Service Training' },
                      { value: 'ppd1', text: 'PPD1' },
                      { value: 'ppd2', text: 'PPD2' },
                      { value: 'bloodTest', text: 'QuantiFERON Blood Test' },
                      { value: 'cxrReport', text: 'CXR Report' },
                      { value: 'drugTest', text: 'Drug Test' },
                      { value: 'fluVaccine', text: 'Flu Vaccine' },
                      { value: 'fluVaccineDeclination', text: 'Flu Vaccine Declination' },
                    ]}
                    isRequired
                  />
                </Col>

                <Col span={6}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="expireStatus"
                    label="Expire Status"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue="ABOUT_TO_EXPIRE"
                    options={[
                      { value: 'ABOUT_TO_EXPIRE', text: 'About to Expire' },
                      { value: 'EXPIRED', text: 'Already Expired' },
                      { value: 'NO_DATE', text: 'No Date Specified' },
                    ]}
                    isRequired
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                backgroundColor="green_haze"
                fontSize="14px"
                type="primary"
                loading={!isSearchButtonEnable}
                htmlType="submit"
              >
                SEARCH
                <Icon type="search" />
              </CustomButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {caregivers &&
          <TableCaregivers
            key={tableKey}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Caregivers"
            caregivers={caregivers}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
SearchExpireCaregiversPage.propTypes = propTypes;

export default Form.create()(SearchExpireCaregiversPage);
